import { useForm } from 'react-hook-form';
import {
  WORKAROUND_TYPES,
  WorkaroundDefinedType,
} from '../../shared/types/business-functions-workarounds';
import { commonFormFields } from '../../pages/cyber/location-applications/shared/workarounds';
import { useSyncRuntimeHours } from '../../pages/cyber';
import { AutomatedFormProps } from '../../components/generic/Form';

/**
 * Generates dynamic form fields based on the current state of the workaround form.
 * This function handles field enabling/disabling based on workaround status and runtime hours configuration.
 *
 * @param {boolean} workaroundNeeded - Whether a workaround is needed
 * @param {boolean} runtimeHoursEnabled - Whether runtime hours field should be enabled
 * @param {boolean} [isProcessArea] - Optional flag indicating if this is a process area form
 * @returns {AutomatedFormProps['fields']} Array of form fields with appropriate disabled states
 */
const getDynamicFormFields = (
  workaroundNeeded: boolean,
  runtimeHoursEnabled: boolean,
  isProcessArea?: boolean,
) => {
  // Add the itApplicationId field for business function workarounds
  const formFields: AutomatedFormProps['fields'] = [
    {
      key: 'itApplicationId',
      hidden: true,
    },
    ...commonFormFields.map((field) => {
      // Create a copy of the field to avoid mutating the original
      const updatedField = { ...field };

      // Disable fields when workaround is not needed
      if (
        field.key !== 'isWorkaroundNeeded' &&
        field.key !== 'mto' &&
        field.key !== 'rto' &&
        field.key !== 'rpo' &&
        field.key !== 'sla'
      ) {
        updatedField.disabled = !workaroundNeeded;
      }

      // Disable runtime hours when not in DEFINED_TIME mode
      if (field.key === 'runtimeHours') {
        updatedField.disabled = !workaroundNeeded || !runtimeHoursEnabled;
      }

      // Update workaroundDefined field to match the business function workarounds format
      if (field.key === 'workaroundDefined') {
        updatedField.options = [
          { label: 'Infinite', value: WorkaroundDefinedType.INFINITE },
          { label: 'Defined Time', value: WorkaroundDefinedType.DEFINED_TIME },
          { label: 'N/A', value: WorkaroundDefinedType.NO_WORKAROUND },
        ];
      }

      // Update field labels to match the business function workarounds format
      if (field.key === 'mto') {
        updatedField.label = 'Maximum Tolerable Outage (MTO)';
        (updatedField as any).tooltip =
          'To fill in this value, you need to go to Data Inputs/IT Applications.';
      }
      if (field.key === 'rto') {
        (updatedField as any).tooltip =
          'To fill in this value, you need to go to Data Inputs/IT Applications.';
      }
      if (field.key === 'rpo') {
        (updatedField as any).tooltip =
          'To fill in this value, you need to go to Data Inputs/IT Applications.';
      }
      if (field.key === 'sla') {
        (updatedField as any).tooltip =
          'To fill in this value, you need to go to Data Inputs/IT Applications.';
      }
      if (field.key === 'maxDataLossHours') {
        updatedField.label = 'Max. Allowable Data Loss';
      }
      if (field.key === 'runtimeHours') {
        (updatedField as any).placeholder = 'hola';
      }

      return updatedField;
    }),
  ];

  if (isProcessArea) {
    formFields.push({
      key: 'applyToAllProcesses',
      label: 'Apply this Workaround to all underlying Processes?',
      type: 'boolean',
      defaultValue: false,
      warning:
        '(If selected yes, this will apply the workaround to all underlying processes and override any existing workarounds.)',
      warningPosition: 'left',
    });
  }

  return formFields;
};

/**
 * Custom hook for managing workaround form state and behavior.
 * Handles form field state, runtime hours synchronization, and process area specific functionality.
 *
 * @param {boolean} [isProcessArea] - Optional flag indicating if this is a process area form
 * @returns {Object} Form state and utilities
 * @returns {Object} return.formInstance - React Hook Form instance
 * @returns {boolean} return.workaroundNeeded - Whether a workaround is needed
 * @returns {string} return.workaroundDefined - Type of workaround defined
 * @returns {number} return.runtimeHours - Runtime hours value
 * @returns {boolean} return.runtimeHoursEnabled - Whether runtime hours field is enabled
 * @returns {AutomatedFormProps['fields']} return.formFields - Array of form fields
 * @returns {Function} return.setValue - Function to set form values
 */
export const useWorkaroundForm = (isProcessArea?: boolean) => {
  const formInstance = useForm();
  const { watch, setValue } = formInstance;

  // Watch form values for useSyncRuntimeHours
  const workaroundNeeded = watch('isWorkaroundNeeded');
  const workaroundDefined = watch('workaroundDefined');
  const runtimeHours = watch('runtimeHours');

  // Calculate if runtime hours should be enabled
  const runtimeHoursEnabled =
    workaroundDefined === WORKAROUND_TYPES.DEFINED_TIME;

  // Get dynamic form fields based on current state
  const formFields = getDynamicFormFields(
    workaroundNeeded,
    runtimeHoursEnabled,
    isProcessArea,
  );

  // Use the useSyncRuntimeHours hook
  useSyncRuntimeHours({
    workaroundNeeded,
    workaroundDefined,
    runtimeHours,
    setValue,
  });

  return {
    formInstance,
    workaroundNeeded,
    workaroundDefined,
    runtimeHours,
    runtimeHoursEnabled,
    formFields,
    setValue,
  };
};
